import { UUID } from 'crypto';
import Decimal from 'decimal.js';
import { AdjustedCargoTemperature } from 'src/types/ViewModel/Stowage/AdjustedCargoTemperature';
import { BlockedTank } from 'src/types/ViewModel/Stowage/BlockedTank';
import { RotationBerthCall } from 'src/types/ViewModel/Stowage/Rotation/RotationBerthCall';
import { RotationCargoOperation } from 'src/types/ViewModel/Stowage/Rotation/RotationCargoOperation';
import { RotationPortCall } from 'src/types/ViewModel/Stowage/Rotation/RotationPortCall';
import { Stowage } from 'src/types/ViewModel/Stowage/Stowage';
import { StowageChangeDataType } from 'src/types/ViewModel/Stowage/Enums/StowageChangeDataType';
import { StowagePlanChange } from 'src/types/ViewModel/Stowage/StowagePlanChange';
import { Violation } from 'src/types/ViewModel/Stowage/Violation/Violation';
import { RotationCargo } from 'src/types/ViewModel/Stowage/Rotation/RotationCargo';
import { getChangeDataGuidKey } from 'src/utils/stowage/stowagePlanChangeUtils/stowageChangeDataTypeUtils';

export type StowageDataItem = {
    changeDataGuid?: UUID;
    stowageItem?: Stowage;
    blockedTank?: BlockedTank;
    adjustedCargoTemperature?: AdjustedCargoTemperature;
    violation?: Violation;
    portCall?: RotationPortCall;
    berthCall?: RotationBerthCall;
    cargoOperation?: RotationCargoOperation;
    cargo?: RotationCargo;
    isDeleted?: boolean;
};

export const mapStowagePlanChangeToStowagePlanDataItem = (stowagePlanChange: StowagePlanChange, keepIfDeleted?: boolean): StowageDataItem => {
    return {
        changeDataGuid: stowagePlanChange.changeDataGuid,
        isDeleted: keepIfDeleted ? stowagePlanChange.isDeleted : undefined,
        stowageItem: mapStowagePlanChangeToDataType(stowagePlanChange, StowageChangeDataType.StowageItem, keepIfDeleted),
        blockedTank: mapStowagePlanChangeToDataType(stowagePlanChange, StowageChangeDataType.BlockedTank, keepIfDeleted),
        adjustedCargoTemperature: mapStowagePlanChangeToDataType(stowagePlanChange, StowageChangeDataType.AdjustedCargoTemperature, keepIfDeleted),
        violation: mapStowagePlanChangeToDataType(stowagePlanChange, StowageChangeDataType.Violation, keepIfDeleted),
        portCall: mapStowagePlanChangeToDataType(stowagePlanChange, StowageChangeDataType.RotationPortCall, keepIfDeleted, { berthCalls: [] }),
        berthCall: mapStowagePlanChangeToDataType(stowagePlanChange, StowageChangeDataType.RotationBerthCall, keepIfDeleted, { cargoOperations: [] }),
        cargoOperation: mapStowagePlanChangeToDataType(stowagePlanChange, StowageChangeDataType.RotationCargoOperation, keepIfDeleted),
        cargo: mapStowagePlanChangeToDataType(stowagePlanChange, StowageChangeDataType.RotationCargo, keepIfDeleted, {
            loadingPortNames: [],
            unloadingPortNames: [],
        }),
    };
};

export const mapStowagePlanChangeToData = (data: string): any => {
    if (!data) return;
    const stowageData = getStowageDataObject<any>(data);

    const returnObject = {} as any;
    Object.keys(stowageData).forEach((key) => {
        if (stowageData[key] !== undefined && stowageData[key] !== null) {
            returnObject[key] = key === 'weight' ? new Decimal(stowageData[key]) : stowageData[key];
        } else {
            returnObject[key] = null;
        }
    });

    return returnObject;
};

export const sortStowagePlanRotationPortCalls = (stowagePlanRotationPortCalls: RotationPortCall[]) => {
    const rotationPortCalls = stowagePlanRotationPortCalls.sort((a, b) => {
        if (a.voyageNumber !== b.voyageNumber) {
            return Number(a.voyageNumber) - Number(b.voyageNumber);
        }
        if (a.portOrderNumber !== b.portOrderNumber) {
            return a.portOrderNumber - b.portOrderNumber;
        }
    });
    rotationPortCalls.forEach((portCall) => {
        portCall.berthCalls = portCall.berthCalls.sort((a, b) => a.berthSequenceNumber - b.berthSequenceNumber);
        portCall.berthCalls.forEach((berthCall) => {
            berthCall.cargoOperations = berthCall.cargoOperations.sort((a, b) => a.cargoOperationSequenceNumber - b.cargoOperationSequenceNumber);
        });
    });

    return rotationPortCalls;
};

const mapStowagePlanChangeToDataType = <T>(
    stowagePlanChange: StowagePlanChange,
    stowageChangeDataType: StowageChangeDataType,
    keepIfDeleted?: boolean,
    extraParams?: any
): T => {
    if (!stowagePlanChange || stowageChangeDataType !== stowagePlanChange.changeDataType) return;
    const changeDataGuidKey = getChangeDataGuidKey(stowagePlanChange.changeDataType);
    if (keepIfDeleted && stowagePlanChange.isDeleted) {
        return {
            [changeDataGuidKey]: stowagePlanChange.changeDataGuid,
        } as T;
    }
    const stowageData = getStowageDataObject<T>(stowagePlanChange.data);
    if ((stowageData as any).weight) {
        (stowageData as any).weight = new Decimal((stowageData as any).weight);
    }
    return {
        ...stowageData,
        ...extraParams,
        [changeDataGuidKey]: stowagePlanChange.changeDataGuid,
    };
};

const getStowageDataObject = <T>(dataJSON: string): T => {
    return JSON.parse(dataJSON);
};
